/**
 * Shared client-side fuzzy search core for the Agion design system.
 *
 * This is the single matching implementation behind every client-side
 * filter surface — the admin-ui DataTable hook (`useFuzzyTableSearch`)
 * AND the `Combobox` / `ComboboxMulti` / `EditableCombobox` option
 * filters. Centralising it here means "type `gpt5`, find `gpt-5`"
 * behaves identically whether the operator is typing into a table
 * search box or a dropdown, in any UI that consumes `@agion/ui`.
 *
 * It is the L1 tier of a three-tier search architecture:
 *
 *   L1  client-side substring + fuzzy (this module)  ≤500 rows in memory
 *   L2  server FTS via `?q=`                          1k–10M rows, ranked
 *   L3  /api/v1/retrieve (semantic + rerank)          cross-domain, intent
 *
 * All three tiers expose the SAME conceptual interface — one `q` string
 * in, a relevance-ordered list out whose elements carry a `score` in
 * `[0, 1]`, and a `mode` discriminator — so a surface can graduate from
 * L1 to L2/L3 without re-modelling its UI.
 *
 * Matching contract (the behaviour every tier should converge on):
 *
 *   * Trim, lowercase, NFKD-strip diacritics so `kuittauksia` matches
 *     `Kuittauksiä` — important for the Finnish / Swedish text we ship.
 *   * Tokenize the query on whitespace AND identifier punctuation
 *     (`-_./:,;|`) so a UUID segment matches the whole UUID.
 *   * Punctuation-insensitive: each query token is also matched against
 *     a separator-collapsed copy of the haystack, so `gpt5` matches
 *     `gpt-5` / `gpt_5` / `gpt 5` and vice-versa.
 *   * Typo-tolerant: a query token with no exact/collapsed substring hit
 *     falls back to a bounded Levenshtein comparison against the
 *     haystack's tokens (edit distance ≤1 for tokens of length ≥4, ≤2
 *     for length ≥8). Catches one-letter spelling mistakes. Fuzzy hits
 *     are weighted strictly below exact hits so they never outrank a
 *     literal match.
 *   * AND of tokens by default; OR available. Both score in `[0, 1]`.
 */

/**
 * Discriminator on every search result. Persists across the L1→L2→L3
 * graduation so consumers can render provenance ("ranked locally" vs
 * "ranked by Postgres FTS" vs "reranked by BGE").
 */
export type SearchMode = 'client_substring' | 'server_fts' | 'retrieve'

export interface SearchHit<T> {
  /** The original row, untouched. */
  row: T
  /** Relevance score in `[0, 1]`. Higher = more relevant. */
  score: number
}

export interface SearchResult<T> {
  /** Ranked hits — score-DESC, ties broken by original order. */
  hits: SearchHit<T>[]
  /** How the ranking was produced. L1 always emits `client_substring`. */
  mode: SearchMode
  /** True when the user entered a query. */
  hasQuery: boolean
}

export interface FuzzySearchOptions<T> {
  /** Builds a string the matcher tokenizes against. Default:
   *  `JSON.stringify(row)` so every visible field becomes searchable.
   *  Pages with derived display strings should pass a custom function
   *  so the search hits the same tokens the operator sees on screen. */
  haystack?: (row: T) => string
  /** AND of tokens by default — every word must appear somewhere.
   *  Set to `or` for "any-token wins" matching. */
  tokens?: 'and' | 'or'
  /** Bounded-Levenshtein typo tolerance for one-letter spelling
   *  mistakes. On by default. Set `false` for surfaces that need strict
   *  substring matching (e.g. exact-id pickers). */
  fuzzy?: boolean
}

const DIACRITIC_REGEX = /[̀-ͯ]/g
const PUNCT_TOKEN_BREAK = /[\s\-_./:,;|]+/
const PUNCT_COLLAPSE = /[\s\-_./:,;|]+/g
// Broader break used only to build the haystack tokens the fuzzy
// (Levenshtein) fallback compares against. The default haystack is
// `JSON.stringify(row)`, so structural punctuation (`"{}[]`, etc.) must
// be stripped or a token reads as `"deployment"}` and never matches a
// clean query word. Letters/digits of every script are preserved.
const HAYSTACK_TOKEN_BREAK = /[^\p{L}\p{N}]+/u

/** Per-token match quality assigned to a Levenshtein (typo) hit before
 *  the edit-distance falloff is applied. Held strictly below 1 so a
 *  fuzzy-only row never outranks a row with a literal substring match. */
const FUZZY_WEIGHT = 0.7

function normalize(input: string): string {
  return input.normalize('NFKD').replace(DIACRITIC_REGEX, '').toLowerCase()
}

function tokenize(input: string): string[] {
  return normalize(input)
    .split(PUNCT_TOKEN_BREAK)
    .filter((t) => t.length > 0)
}

/** Strip every separator so `gpt-5`, `gpt_5` and `gpt 5` all fold to
 *  `gpt5`. Run on an already-normalized string. */
function collapseSeparators(normalized: string): string {
  return normalized.replace(PUNCT_COLLAPSE, '')
}

/** Edit-distance budget for a query token. Short tokens get none —
 *  one edit on a 3-char token matches too much to be useful. */
function fuzzyBudget(tokenLength: number): number {
  if (tokenLength >= 8) return 2
  if (tokenLength >= 4) return 1
  return 0
}

/**
 * Levenshtein distance bounded by `max`: returns `max + 1` as soon as
 * the best achievable distance provably exceeds the budget, so the DP
 * bails early instead of filling the whole matrix.
 */
function boundedLevenshtein(a: string, b: string, max: number): number {
  const la = a.length
  const lb = b.length
  if (Math.abs(la - lb) > max) return max + 1
  if (la === 0) return lb
  if (lb === 0) return la

  // Int32Array element access is `number` (not `number | undefined`)
  // under noUncheckedIndexedAccess, and avoids per-cell allocation.
  let prev = new Int32Array(lb + 1)
  let curr = new Int32Array(lb + 1)
  for (let j = 0; j <= lb; j++) prev[j] = j

  for (let i = 1; i <= la; i++) {
    curr[0] = i
    let rowMin = i
    const ca = a.charCodeAt(i - 1)
    for (let j = 1; j <= lb; j++) {
      const cost = ca === b.charCodeAt(j - 1) ? 0 : 1
      // `?? 0` is unreachable (every index is in-bounds) but satisfies
      // noUncheckedIndexedAccess without non-null assertions.
      const del = (prev[j] ?? 0) + 1
      const ins = (curr[j - 1] ?? 0) + 1
      const sub = (prev[j - 1] ?? 0) + cost
      const v = Math.min(del, ins, sub)
      curr[j] = v
      if (v < rowMin) rowMin = v
    }
    // Whole row already over budget — no later row can recover.
    if (rowMin > max) return max + 1
    const tmp = prev
    prev = curr
    curr = tmp
  }
  return prev[lb] ?? 0
}

/**
 * Best match quality in `[0, 1]` for a single query token against one
 * row's haystack. Exact substring (raw or separator-collapsed) returns
 * a `{ quality: 1, index }` so callers can award the position bonus;
 * a bounded-Levenshtein fallback returns a sub-1 quality with no index.
 * Returns `quality: 0` for no match.
 */
function matchToken(
  token: string,
  haystack: string,
  collapsedHaystack: string,
  haystackTokens: string[],
  fuzzy: boolean,
): { quality: number; index: number } {
  const idx = haystack.indexOf(token)
  if (idx >= 0) return { quality: 1, index: idx }

  const collapsedToken = collapseSeparators(token)
  if (collapsedToken.length > 0) {
    const cIdx = collapsedHaystack.indexOf(collapsedToken)
    if (cIdx >= 0) return { quality: 1, index: cIdx }
  }

  if (!fuzzy) return { quality: 0, index: -1 }

  const budget = fuzzyBudget(token.length)
  if (budget === 0) return { quality: 0, index: -1 }

  let best = 0
  for (const ht of haystackTokens) {
    // A one-edit match against a 3-char word is too loose (`gpt5`↔`gpt`,
    // `bat`↔`cat`): only fuzzy-match reasonably-sized targets.
    if (ht.length < 4) continue
    if (Math.abs(ht.length - token.length) > budget) continue
    const dist = boundedLevenshtein(token, ht, budget)
    if (dist <= budget) {
      // Falloff by edits-per-char keeps a 1-edit typo well above a
      // 2-edit one, and the FUZZY_WEIGHT cap keeps both below exact.
      const quality = FUZZY_WEIGHT * (1 - dist / Math.max(token.length, ht.length))
      if (quality > best) best = quality
      if (dist === 1) break // good enough; stop scanning this row
    }
  }
  return { quality: best, index: -1 }
}

function defaultHaystack<T>(row: T): string {
  try {
    return JSON.stringify(row)
  } catch {
    // Circular ref or non-serialisable — fall back to a coercion that
    // at least covers scalar fields.
    return String(row)
  }
}

/**
 * Pure scoring function — no React, so unit tests and non-hook callers
 * (e.g. design-system Combobox filters) can use it directly.
 */
export function scoreRows<T>(
  rows: T[],
  query: string,
  options: FuzzySearchOptions<T> = {},
): SearchResult<T> {
  const haystackOf = options.haystack ?? defaultHaystack
  const queryTokens = tokenize(query)

  if (queryTokens.length === 0) {
    return {
      hits: rows.map((row) => ({ row, score: 1 })),
      mode: 'client_substring',
      hasQuery: false,
    }
  }

  const matcher = options.tokens === 'or' ? 'or' : 'and'
  const fuzzy = options.fuzzy !== false
  const out: SearchHit<T>[] = []

  for (const row of rows) {
    const haystack = normalize(haystackOf(row))
    const collapsedHaystack = collapseSeparators(haystack)
    // Haystack tokens are only needed for the Levenshtein fallback, so
    // build them lazily and dedupe to bound the per-row work.
    let haystackTokens: string[] | null = null
    const tokensOf = () => {
      if (haystackTokens === null) {
        haystackTokens = Array.from(new Set(haystack.split(HAYSTACK_TOKEN_BREAK))).filter(
          (t) => t.length > 0,
        )
      }
      return haystackTokens
    }

    let matchedCount = 0
    let qualitySum = 0
    let positionBonus = 0
    for (const token of queryTokens) {
      const { quality, index } = matchToken(
        token,
        haystack,
        collapsedHaystack,
        // Only pay for tokenization if we reach the fuzzy branch.
        fuzzy ? tokensOf() : [],
        fuzzy,
      )
      if (quality > 0) {
        matchedCount += 1
        qualitySum += quality
        if (index >= 0) {
          // Earlier matches get a small bonus; the divisor keeps the
          // bonus capped at ~0.05 even for a token at offset 0.
          positionBonus += 1 / (1 + index / 50)
        }
      }
    }
    if (matcher === 'and' && matchedCount < queryTokens.length) continue
    if (matcher === 'or' && matchedCount === 0) continue

    const baseScore = qualitySum / queryTokens.length
    // Score in [0, 1.05]; clamp to 1 so the contract holds.
    const score = Math.min(1, baseScore + positionBonus * 0.05)
    out.push({ row, score })
  }

  // Stable sort: score DESC, ties keep input order (browser sorts are
  // stable as of ES2019).
  out.sort((a, b) => b.score - a.score)

  return { hits: out, mode: 'client_substring', hasQuery: true }
}

/**
 * Convenience helper for list/table surfaces that only need the matching
 * rows, relevance-ordered while a query is active.
 */
export function filterRowsBySearchBody<T>(
  rows: T[],
  query: string,
  options: FuzzySearchOptions<T> = {},
): T[] {
  return scoreRows(rows, query, options).hits.map((hit) => hit.row)
}

/** Minimal shape the combobox option filter searches over. */
export interface SearchableOption {
  label: string
  value: string
  description?: string
}

/**
 * Filter combobox-style options with the shared L1 matcher so dropdown
 * search behaves identically to table search (diacritic folding,
 * punctuation-insensitive, typo-tolerant, relevance-ranked). Searches
 * the label, value and description of each option.
 */
export function filterComboboxOptions<T extends SearchableOption>(
  options: T[],
  query: string,
  fuzzyOptions: Pick<FuzzySearchOptions<T>, 'fuzzy'> = {},
): T[] {
  return filterRowsBySearchBody(options, query, {
    haystack: (o) => [o.label, o.value, o.description].filter(Boolean).join(' '),
    fuzzy: fuzzyOptions.fuzzy,
  })
}
